import type { InviteJudgeRequest, JudgeInvitationDto, MyJudgeInvitationDto } from '@napaicp/schemas'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { competitionKeys } from '@/features/competitions/query-keys'
import { apiRequest } from '@/lib/api-client'

const base = (competitionId: string) => `/api/competitions/${competitionId}/judge-invitations`
const keys = {
  list: (competitionId: string) => ['competitions', competitionId, 'judge-invitations'] as const,
}

/** ISSUE-PREVIEW #9: lets an admin invite a not-yet-registered email as a judge — the server bootstraps the account and a Temporary Initial Password, emailed directly to the judge; the admin never sees it. */
export function useJudgeInvitations(competitionId: string): UseQueryResult<JudgeInvitationDto[]> {
  return useQuery({
    queryKey: keys.list(competitionId),
    queryFn: () => apiRequest<JudgeInvitationDto[]>(base(competitionId)),
    enabled: competitionId.length > 0,
  })
}

export function useInviteJudge(competitionId: string) {
  const query = useQueryClient()
  return useMutation({
    mutationFn: (body: InviteJudgeRequest) =>
      apiRequest<JudgeInvitationDto>(base(competitionId), { method: 'POST', body }),
    onSuccess: () => query.invalidateQueries({ queryKey: keys.list(competitionId) }),
  })
}

export function useResendJudgeInvitation(competitionId: string) {
  const query = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiRequest<JudgeInvitationDto>(`${base(competitionId)}/${invitationId}/resend`, {
        method: 'POST',
      }),
    onSuccess: () => query.invalidateQueries({ queryKey: keys.list(competitionId) }),
  })
}

export function useCancelJudgeInvitation(competitionId: string) {
  const query = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiRequest<{ id: string; cancelled: boolean }>(`${base(competitionId)}/${invitationId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => query.invalidateQueries({ queryKey: keys.list(competitionId) }),
  })
}

const mineKey = ['judge-invitations', 'mine'] as const

/** The signed-in person's own pending judge invitations (matched by their verified email). */
export function useMyJudgeInvitations(): UseQueryResult<MyJudgeInvitationDto[]> {
  return useQuery({
    queryKey: mineKey,
    queryFn: () => apiRequest<MyJudgeInvitationDto[]>('/api/judge-invitations/mine'),
    retry: false,
  })
}

export function useAcceptMyJudgeInvitation() {
  const query = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiRequest<{ competitionId: string; accepted: boolean }>(
        `/api/judge-invitations/${invitationId}/accept`,
        { method: 'POST' },
      ),
    onSuccess: async () => {
      await query.invalidateQueries({ queryKey: mineKey })
      // The judge role is new: refresh the competition list so the workspace appears.
      await query.invalidateQueries({ queryKey: competitionKeys.all })
    },
  })
}

export function useDeclineMyJudgeInvitation() {
  const query = useQueryClient()
  return useMutation({
    mutationFn: ({ invitationId, reason }: { invitationId: string; reason: string }) =>
      apiRequest<{ id: string; declined: boolean }>(
        `/api/judge-invitations/${invitationId}/decline`,
        { method: 'POST', body: reason.trim().length > 0 ? { reason: reason.trim() } : {} },
      ),
    onSuccess: () => query.invalidateQueries({ queryKey: mineKey }),
  })
}
